/**
 * Assistant MCP-discovery mutation hooks (AC-1738, epic AC-1732).
 *
 * Backs the human-approval gate for a Tier-3 remote-MCP proposal surfaced by
 * `McpProposalCard` inside `AssistantChat`:
 *
 *   - `useConfirmMcpProposal()` -> POST .../actions/{aid}/confirm-mcp
 *   - `useDeclineMcpProposal()` -> POST .../actions/{aid}/decline-mcp
 *
 * Discovery/proposal data is server state owned by React Query (it arrives on
 * the chat turn and is replayed by these mutations); the ephemeral form state
 * (chosen scope, entered credential) lives locally in `McpProposalCard`.
 *
 * ## Invalidation contract
 *
 * Both mutations are pessimistic — the server's response is the truth. On
 * SUCCESS each invalidates `queryKeys.mcpServers()` so a newly provisioned (or,
 * for decline, the unchanged) server list re-reads and the new server appears in
 * the integrations UI without a manual refetch (AC-1738 acceptance). On FAILURE
 * `mutation.error`/`isError` surface and NOTHING is invalidated — a failed
 * mutation must not perturb the cache (decline-without-residue /
 * no-spurious-refetch invariant). React Query only runs `onSuccess` when the
 * `mutationFn` resolves, so the invalidation is automatically skipped on reject.
 *
 * Structure mirrors `useSetAssistantModelPreference` (single pessimistic write
 * with an explicit `onSuccess` invalidation set).
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import apiService from '../../services/api';
import {
  AssistantChatResponse,
  McpProvisionRequest,
  McpConfigureBuiltinRequest,
} from '../../types';
import { queryKeys } from './queryKeys';

/** The Email built-in server id; its credential status read is keyed on this. */
const EMAIL_SERVER_ID = -2;

/** Args for `useConfirmMcpProposal().mutate(...)`. */
export interface ConfirmMcpProposalVars {
  conversationId: number;
  actionId: number;
  request: McpProvisionRequest;
}

/** Args for `useDeclineMcpProposal().mutate(...)`. */
export interface DeclineMcpProposalVars {
  conversationId: number;
  actionId: number;
}

/**
 * `useConfirmMcpProposal()` — confirms a pending `propose_mcp_server` action,
 * provisioning the remote server with the chosen scope + credentials. On success
 * invalidates `queryKeys.mcpServers()` so the new server surfaces in
 * integrations. On failure surfaces `mutation.error` and invalidates nothing.
 */
export function useConfirmMcpProposal(): UseMutationResult<
  AssistantChatResponse,
  Error,
  ConfirmMcpProposalVars
> {
  const queryClient = useQueryClient();
  return useMutation<AssistantChatResponse, Error, ConfirmMcpProposalVars>({
    mutationFn: ({ conversationId, actionId, request }) =>
      apiService.confirmMcpProposal(conversationId, actionId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mcpServers() });
    },
  });
}

/**
 * `useDeclineMcpProposal()` — declines a pending `propose_mcp_server` action so
 * nothing is provisioned (decline-without-residue). On success invalidates
 * `queryKeys.mcpServers()`; on failure surfaces `mutation.error` and invalidates
 * nothing.
 */
export function useDeclineMcpProposal(): UseMutationResult<
  AssistantChatResponse,
  Error,
  DeclineMcpProposalVars
> {
  const queryClient = useQueryClient();
  return useMutation<AssistantChatResponse, Error, DeclineMcpProposalVars>({
    mutationFn: ({ conversationId, actionId }) =>
      apiService.declineMcpProposal(conversationId, actionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mcpServers() });
    },
  });
}

/** Args for `useConfirmBuiltinSetup().mutate(...)`. */
export interface ConfirmBuiltinSetupVars {
  conversationId: number;
  actionId: number;
  request: McpConfigureBuiltinRequest;
}

/** Args for `useDeclineBuiltinSetup().mutate(...)`. */
export interface DeclineBuiltinSetupVars {
  conversationId: number;
  actionId: number;
}

/**
 * `useConfirmBuiltinSetup()` (AC-1746, defect AC-1743) — confirms a pending
 * `configure_builtin_mcp` action, connecting an unconfigured built-in (e.g.
 * Email) from inside the assistant chat by saving its credentials (encrypted,
 * via `BuiltinCredentialService` server-side). On SUCCESS invalidates BOTH
 * `queryKeys.builtinCredentials(-2)` (so any "is this built-in configured?" read
 * re-runs) AND `queryKeys.mcpServers()` (so the integrations list refreshes). On
 * FAILURE surfaces `mutation.error` and invalidates nothing — a failed mutation
 * must not perturb the cache (decline-without-residue / no-spurious-refetch).
 */
export function useConfirmBuiltinSetup(): UseMutationResult<
  AssistantChatResponse,
  Error,
  ConfirmBuiltinSetupVars
> {
  const queryClient = useQueryClient();
  return useMutation<AssistantChatResponse, Error, ConfirmBuiltinSetupVars>({
    mutationFn: ({ conversationId, actionId, request }) =>
      apiService.confirmBuiltinSetup(conversationId, actionId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.builtinCredentials(
          variables.request.builtin_server_id ?? EMAIL_SERVER_ID,
        ),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.mcpServers() });
    },
  });
}

/**
 * `useDeclineBuiltinSetup()` (AC-1746, defect AC-1743) — declines a pending
 * `configure_builtin_mcp` action so nothing is saved (decline-without-residue).
 * On SUCCESS invalidates `queryKeys.builtinCredentials(-2)` AND
 * `queryKeys.mcpServers()` so the (unchanged) reads re-settle; on FAILURE
 * surfaces `mutation.error` and invalidates nothing.
 */
export function useDeclineBuiltinSetup(): UseMutationResult<
  AssistantChatResponse,
  Error,
  DeclineBuiltinSetupVars
> {
  const queryClient = useQueryClient();
  return useMutation<AssistantChatResponse, Error, DeclineBuiltinSetupVars>({
    mutationFn: ({ conversationId, actionId }) =>
      apiService.declineBuiltinSetup(conversationId, actionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.builtinCredentials(EMAIL_SERVER_ID),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.mcpServers() });
    },
  });
}

export default useConfirmMcpProposal;
